/**
 * Generalizes the inline reauth prompt that used to live only in OrderDetailPage's refund modal
 * into one shared mechanism `lib/api.ts`'s `request()` itself can call for ANY action, not just
 * refunds - this is what "build this once and use it for every reauth-protected action" means in
 * practice: `request()` detects a reauth-required response (now reliably, via
 * `details.reauthRequired` - apps/api/src/middleware/reauth.ts) and calls `promptForReauth()`
 * below itself, then retries the original request once with the token attached. No per-page
 * wiring is needed for this to work - every mutation already goes through `request()`.
 *
 * Same pub-sub shape as `mode.ts`'s `subscribeMode` - `<GlobalReauthDialog>` (components/
 * GlobalReauthDialog.tsx) is the one place that actually renders the dialog, mounted once near
 * the app root.
 */

export interface ReauthControllerState {
  open: boolean;
  pending: boolean;
  error?: string;
}

let state: ReauthControllerState = { open: false, pending: false };
const listeners = new Set<(s: ReauthControllerState) => void>();
let pendingResolver: { resolve: () => void; reject: (err: Error) => void } | null = null;

function setState(patch: Partial<ReauthControllerState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l(state);
}

export function subscribeReauthController(listener: (s: ReauthControllerState) => void): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}

/** Opens the dialog and returns a promise that resolves once a fresh reauth token has been
 * obtained and stored (via `lib/reauth.ts`'s `setReauthToken`) - the caller (`request()`) should
 * then simply retry its original fetch, which will pick up the new token automatically. */
export function promptForReauth(): Promise<void> {
  setState({ open: true, pending: false, error: undefined });
  return new Promise<void>((resolve, reject) => {
    pendingResolver = { resolve, reject };
  });
}

/** Called by the submit handler in GlobalReauthDialog once `POST /dashboard/auth/reauth`
 * actually succeeds and the new token is already stored. */
export function resolveReauthPrompt(): void {
  setState({ open: false, pending: false, error: undefined });
  pendingResolver?.resolve();
  pendingResolver = null;
}

export function failReauthAttempt(message: string): void {
  setState({ pending: false, error: message });
}

export function setReauthPending(pending: boolean): void {
  setState({ pending });
}

export function cancelReauthPrompt(): void {
  setState({ open: false, pending: false, error: undefined });
  pendingResolver?.reject(new Error('Re-authentication was cancelled.'));
  pendingResolver = null;
}
